import { useState, useEffect } from 'react';

// components
import Image from 'next/image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';
import { useAppContext } from '@/components/utilities/AppContext';

const TestimonialsBlock = ({ testimonialsCarouselContent: content }) => {
  const { locale } = useAppContext();
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollSnaps = () => {
      setScrollSnaps(api.scrollSnapList());
    };

    // Initialize state from external carousel API
    // setTimeout defers state updates to avoid cascading renders
    setTimeout(() => {
      updateScrollSnaps();
      setCurrent(api.selectedScrollSnap() + 1);
    }, 0);

    // Subscribe to carousel events (external system updates)
    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', handleSelect);

    const handleResize = () => {
      setTimeout(updateScrollSnaps, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      api.off('select', handleSelect);
      window.removeEventListener('resize', handleResize);
    };
  }, [api]);

  const onSelectItem = (e) => {
    const index = Number(e.currentTarget.dataset.id) || 0;

    if (api) {
      api.scrollTo(index);
    }
  };

  const onPrev = () => {
    if (api) {
      api.scrollPrev();
    }
  };

  const onNext = () => {
    if (api) {
      api.scrollNext();
    }
  };

  return (
    <VisibleLoading delay={300}>
      {({ ref, className }) => (
        <div ref={ref} className={`mx-auto mt-4 mb-20 w-full xl:w-(--container-width) ${className || ''}`.trim()}>
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              breakpoints: {
                '(max-width: 1024px)': {
                  align: 'center'
                }
              }
            }}
          >
            <CarouselContent className="mr-4 ml-0 py-16 lg:mx-2.5">
              {(content || []).map((item, index) => (
                <CarouselItem key={item.id} className="basis-[90vw] lg:basis-1/2 lg:px-3">
                  <TestimonialItem key={item.id} index={index} testimonial={item} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="mx-auto flex items-center justify-center gap-12 lg:gap-42">
            <button
              type="button"
              onClick={onPrev}
              aria-label={locale !== 'pl' ? 'Předchozí reference' : 'Poprzednia referencja'}
              className="cursor-pointer bg-transparent p-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="27"
                height="27"
                viewBox="0 0 27 27"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="13.3335"
                  y="26.333"
                  width="18.62"
                  height="2.82122"
                  transform="rotate(-135 13.3335 26.333)"
                  className="fill-grey-950"
                />
                <rect
                  x="15.3284"
                  y="1.99512"
                  width="18.62"
                  height="2.82122"
                  transform="rotate(135 15.3284 1.99512)"
                  className="fill-grey-950"
                />
              </svg>
            </button>

            <div className="flex items-center justify-center gap-2">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  data-id={index}
                  aria-label={`${locale !== 'pl' ? 'Zobrazit referenci' : 'Pokaż referencję'} ${index + 1}`}
                  aria-current={current === index + 1}
                  className="group flex cursor-pointer items-center justify-center bg-transparent p-2"
                  onClick={onSelectItem}
                >
                  <div
                    className={`bg-grey-950 h-2 w-2 rounded-full transition-transform group-hover:scale-125 group-hover:opacity-100 ${current === index + 1 ? 'scale-125 opacity-100' : 'opacity-15'} `}
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={onNext}
              aria-label={locale !== 'pl' ? 'Další reference' : 'Następna referencja'}
              className="cursor-pointer bg-transparent p-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="27"
                height="27"
                viewBox="0 0 27 27"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="13.6663"
                  width="18.62"
                  height="2.82122"
                  transform="rotate(45 13.6663 0)"
                  className="fill-grey-950"
                />
                <rect
                  x="11.6714"
                  y="24.3379"
                  width="18.62"
                  height="2.82122"
                  transform="rotate(-45 11.6714 24.3379)"
                  className="fill-grey-950"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </VisibleLoading>
  );
};
export default TestimonialsBlock;

const TestimonialItem = ({ testimonial, index }) => {
  let quoteColor = 'fill-brand-400';
  if (testimonial.color === 'blue') {
    quoteColor = 'fill-blue-400';
  } else if (testimonial.color === 'green') {
    quoteColor = 'fill-green-500';
  } else if (testimonial.color === 'purple') {
    quoteColor = 'fill-purple-500';
  }

  if (!testimonial?.testimonial) {
    return null;
  }

  return (
    <div className="h-full justify-between">
      <div className="flex h-full flex-col rounded-3xl bg-white p-6 shadow-2xl/15 xl:p-10">
        <div className="mb-6 h-[28px] text-[135px] xl:mb-8">
          <svg width="39" height="28" viewBox="0 0 39 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16.84 20.025C16.84 24.48 13.735 27.855 9.01 27.855C4.285 27.855 0.235001 24.075 0.235001 17.325C0.235001 8.95499 6.58 1.8 14.815 0.449997V5.445C10.09 6.38999 6.58 9.765 6.58 13.68C7.39 13.14 8.47 12.735 10.225 12.735C13.735 12.735 16.84 15.3 16.84 20.025ZM38.71 20.025C38.71 24.48 35.47 27.855 30.745 27.855C26.155 27.855 21.97 24.075 21.97 17.325C21.97 8.95499 28.315 1.8 36.685 0.449997V5.445C31.96 6.38999 28.45 9.765 28.45 13.545C29.26 13.005 30.475 12.735 32.095 12.735C35.605 12.735 38.71 15.3 38.71 20.025Z"
              className={quoteColor}
            />
          </svg>
        </div>
        <div className="txt-lg xl:txt-xl flex-1 text-black">{testimonial.testimonial.message}</div>
        <div className="mt-8 flex items-center gap-3">
          <div className="h-12 w-12 overflow-hidden rounded-full bg-white/[20%]">
            {testimonial.testimonial.image && (
              <Image
                src={getImagePath(testimonial.testimonial.image)}
                alt={testimonial.testimonial.name}
                width={testimonial.testimonial.image.width}
                height={testimonial.testimonial.image.height}
                sizes="48px"
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div>
            <div className="txt-xl font-medium text-black">{testimonial.testimonial.name}</div>
            <div className="txt-lg text-black-50">{testimonial.testimonial.company}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
